import { domainPath } from "@/lib/domainLinks";

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, GraduationCap, Building2, FolderTree } from "lucide-react";
import api from "@/lib/api";

export default function SearchBox({ className = "", placeholder = "Search courses, institutes…" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const onChange = (v) => {
    setQ(v);
    clearTimeout(timer.current);
    if (v.trim().length < 2) { setResults(null); return; }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search/suggest?q=${encodeURIComponent(v.trim())}`);
        setResults(data);
        setOpen(true);
      } catch (e) {}
    }, 250);
  };

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };
  const hasResults = results && (results.programs.length || results.categories.length || results.institutes.length);

  return (
    <div ref={boxRef} className={`relative ${className}`} data-testid="header-search">
      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
      <input value={q} onChange={(e) => onChange(e.target.value)} onFocus={() => results && setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) go(`/courses?q=${encodeURIComponent(q.trim())}`); }}
        placeholder={placeholder} data-testid="header-search-input"
        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:bg-white transition-all" />

      {open && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl border border-slate-200 shadow-2xl p-3 z-50 max-h-[420px] overflow-y-auto" data-testid="search-suggestions">
          {!hasResults && <p className="text-sm text-slate-400 px-3 py-4">No matches — press Enter to search all programs.</p>}
          {results?.programs?.length > 0 && (
            <div className="mb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-3 py-1.5">Programs</p>
              {results.programs.map((p) => (
                <button key={p.slug} onClick={() => go(p.url_path || `/courses/${p.slug}`)} data-testid={`suggest-program-${p.slug}`}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-rose-50 text-left">
                  <GraduationCap className="w-4 h-4 text-rose-500 shrink-0" />
                  <span className="min-w-0"><span className="block text-sm font-semibold text-slate-800 truncate">{p.title}</span>
                    <span className="block text-xs text-slate-400 truncate">{p.university}</span></span>
                </button>
              ))}
            </div>
          )}
          {results?.categories?.length > 0 && (
            <div className="mb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-3 py-1.5">Domains</p>
              {results.categories.map((c) => (
                <button key={c.slug} onClick={() => go(domainPath(c.slug))} data-testid={`suggest-category-${c.slug}`}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-rose-50 text-left">
                  <FolderTree className="w-4 h-4 text-amber-500 shrink-0" />
                  <span className="text-sm font-semibold text-slate-800">{c.name}</span>
                </button>
              ))}
            </div>
          )}
          {results?.institutes?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-3 py-1.5">Institutes</p>
              {results.institutes.map((i) => (
                <button key={i.slug} onClick={() => go(`/institutes/${i.slug}`)} data-testid={`suggest-institute-${i.slug}`}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-rose-50 text-left">
                  <Building2 className="w-4 h-4 text-slate-500 shrink-0" />
                  <span className="text-sm font-semibold text-slate-800">{i.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
